import { createFeatureSelector, createSelector } from '@ngrx/store';
import * as fromVehicleGroups from 'app/store/temporary/reducers/vehicle-groups.reducer';
import * as fromVehicleTypes from 'app/store/temporary/reducers/vehicle-types.reducer';
import * as fromProductGroups from 'app/store/temporary/reducers/product-groups.reducer';
import * as fromCustomers from 'app/store/temporary/reducers/customers.reducer';
import * as fromTransportSites from 'app/store/temporary/reducers/transport-sites.reducer';
import * as fromCustomerProjects from 'app/store/temporary/reducers/customer-projects.reducer';
import * as fromDepartments from 'app/store/temporary/reducers/departments.reducer';
import * as fromProjects from 'app/store/temporary/reducers/projects.reducer';
import * as fromAssignments from 'app/store/temporary/reducers/assignments.reducer';
import * as fromVehicles from 'app/store/temporary/reducers/vehicles.reducer';
import * as fromProducts from 'app/store/temporary/reducers/products.reducer';
import * as fromGoogleMaps from 'app/store/temporary/reducers/google-maps.reducer';
import * as fromOrders from 'app/store/temporary/reducers/orders.reducer';
import * as fromOrderLines from 'app/store/temporary/reducers/order-lines.reducer';
import * as fromDrivers from 'app/store/temporary/reducers/drivers.reducer';
import * as fromAddresses from 'app/store/temporary/reducers/addresses.reducer';
import * as fromDeliveries from 'app/store/temporary/reducers/deliveries.reducer';
import * as fromSuppliers from 'app/store/temporary/reducers/suppliers.reducer';
import * as fromContacts from 'app/store/temporary/reducers/contacts.reducer';
import * as fromDeviations from 'app/store/temporary/reducers/deviations.reducer';
import * as fromUnits from 'app/store/temporary/reducers/units.reducer';
import * as fromDeliveryTimeCategories from 'app/store/temporary/reducers/delivery-time-categories.reducer';
import * as fromExportBatches from 'app/store/temporary/reducers/export-batches.reducer';
import * as fromDeliveryTimes from './delivery-times.reducer';
import * as fromParsedAddress from './parsed-address.reducer';
import * as fromTerminalTransactions from './terminal-transactions.reducer';
import * as fromPageFilters from './page-filters.reducer';

export interface TemporaryState {
  vehicleGroups: fromVehicleGroups.State;
  vehicleTypes: fromVehicleTypes.State;
  productGroups: fromProductGroups.State;
  customers: fromCustomers.State;
  transportSites: fromTransportSites.State;
  customerProjects: fromCustomerProjects.State;
  departments: fromDepartments.State;
  projects: fromProjects.State;
  assignments: fromAssignments.State;
  vehicles: fromVehicles.State;
  products: fromProducts.State;
  googleMaps: fromGoogleMaps.State;
  orders: fromOrders.State;
  orderLines: fromOrderLines.State;
  drivers: fromDrivers.State;
  addresses: fromAddresses.State;
  deliveries: fromDeliveries.State;
  suppliers: fromSuppliers.State;
  contacts: fromContacts.State;
  deviations: fromDeviations.State;
  units: fromUnits.State;
  deliveryTimeCategories: fromDeliveryTimeCategories.State;
  exportBatches: fromExportBatches.State;
  deliveryTimes: fromDeliveryTimes.State;
  parsedAddress: fromParsedAddress.State;
  terminalTransactions: fromTerminalTransactions.State;
  pageFilters: fromPageFilters.State;
}

export const temporaryReducers = {
  vehicleGroups: fromVehicleGroups.reducer,
  vehicleTypes: fromVehicleTypes.reducer,
  productGroups: fromProductGroups.reducer,
  customers: fromCustomers.reducer,
  transportSites: fromTransportSites.reducer,
  customerProjects: fromCustomerProjects.reducer,
  departments: fromDepartments.reducer,
  projects: fromProjects.reducer,
  assignments: fromAssignments.reducer,
  vehicles: fromVehicles.reducer,
  products: fromProducts.reducer,
  googleMaps: fromGoogleMaps.reducer,
  orders: fromOrders.reducer,
  orderLines: fromOrderLines.reducer,
  drivers: fromDrivers.reducer,
  addresses: fromAddresses.reducer,
  deliveries: fromDeliveries.reducer,
  suppliers: fromSuppliers.reducer,
  contacts: fromContacts.reducer,
  deviations: fromDeviations.reducer,
  units: fromUnits.reducer,
  deliveryTimeCategories: fromDeliveryTimeCategories.reducer,
  exportBatches: fromExportBatches.reducer,
  deliveryTimes: fromDeliveryTimes.reducer,
  parsedAddress: fromParsedAddress.reducer,
  terminalTransactions: fromTerminalTransactions.reducer,
  pageFilters: fromPageFilters.reducer,
};

export const getRootAppState =
  createFeatureSelector<TemporaryState>('temporary');

// Vehicle groups states
export const getVehicleGroupsState = createSelector(
  getRootAppState,
  (state) => state.vehicleGroups
);

export const getVehicleGroupsList = createSelector(
  getVehicleGroupsState,
  (state) => state.vehicleGroups
);

export const getIsVehicleGroupsSaving = createSelector(
  getVehicleGroupsState,
  (state) => state.isSaving
);

// Vehicle types states
export const getVehicleTypesState = createSelector(
  getRootAppState,
  (state) => state.vehicleTypes
);

export const getVehicleTypesList = createSelector(
  getVehicleTypesState,
  (state) => state.vehicleTypes
);

export const getIsVehicleTypesReady = createSelector(
  getVehicleTypesState,
  (state) => state.isReady
);

export const getIsVehicleTypesSaving = createSelector(
  getVehicleTypesState,
  (state) => state.isSaving
);

// Product Groups
export const getProductGroupsState = createSelector(
  getRootAppState,
  (state) => state.productGroups
);

export const getProductGroups = createSelector(
  getProductGroupsState,
  (state) => state.productGroups
);

export const getIsProductGroupsSaving = createSelector(
  getProductGroupsState,
  (state) => state.isSaving
);

// Customers
export const getCustomersState = createSelector(
  getRootAppState,
  (state) => state.customers
);

export const getCustomers = createSelector(
  getCustomersState,
  (state) => state.customers
);

export const getTotalCustomers = createSelector(
  getCustomersState,
  (state) => state.totalCustomers
);

export const getIsCustomersReady = createSelector(
  getCustomersState,
  (state) => state.isReady
);

export const getIsCustomersSaving = createSelector(
  getCustomersState,
  (state) => state.isSaving
);

// Transport sites
export const getTransportSiteState = createSelector(
  getRootAppState,
  (state) => state.transportSites
);

export const getTransportSites = createSelector(
  getTransportSiteState,
  (state) => state.transportSites
);

export const getIsTransportSitesReady = createSelector(
  getTransportSiteState,
  (state) => state.isReady
);

// Customer projects
export const getCustomerProjectsState = createSelector(
  getRootAppState,
  (state) => state.customerProjects
);

export const getCustomerProjects = createSelector(
  getCustomerProjectsState,
  (state) => state.customerProjects
);

export const getIsCustomerProjectsReady = createSelector(
  getCustomerProjectsState,
  (state) => state.isReady
);

export const getIsCustomerProjectsSaving = createSelector(
  getCustomerProjectsState,
  (state) => state.isSaving
);

// Departments
export const getDepartmentsState = createSelector(
  getRootAppState,
  (state) => state.departments
);

export const getDepartments = createSelector(
  getDepartmentsState,
  (state) => state.departments
);

export const getIsDepartmentsReady = createSelector(
  getDepartmentsState,
  (state) => state.isReady
);

// Projects
export const getProjectsState = createSelector(
  getRootAppState,
  (state) => state.projects
);

export const getProjects = createSelector(
  getProjectsState,
  (state) => state.projects
);

export const getTotalProjectsCount = createSelector(
  getProjectsState,
  (state) => state.totalProjects
);

export const isProjectsLoading = createSelector(
  getProjectsState,
  (state) => state.isLoading
);

export const getIsProjectsReady = createSelector(
  getProjectsState,
  (state) => state.isReady
);

export const getIsProjectsSaving = createSelector(
  getProjectsState,
  (state) => state.isSaving
);

// Assignments
export const getAssignmentsState = createSelector(
  getRootAppState,
  (state) => state.assignments
);

export const getIsAssignmentsSaving = createSelector(
  getAssignmentsState,
  (state) => state.isSaving
);

// Vehicles
export const getVehiclesState = createSelector(
  getRootAppState,
  (state) => state.vehicles
);

export const isVehiclesLoading = createSelector(
  getVehiclesState,
  (state) => state.isLoading
);

export const getIsVehiclesReady = createSelector(
  getVehiclesState,
  (state) => state.isReady
);

export const getVehicles = createSelector(
  getVehiclesState,
  (state) => state.vehicles
);

export const getTotalVehicleCount = createSelector(
  getVehiclesState,
  (state) => state.totalVehicles
);

export const getIsVehiclesSaving = createSelector(
  getVehiclesState,
  (state) => state.isSaving
);

// Products
export const getProductsPageState = createSelector(
  getRootAppState,
  (state) => state.products
);

export const getProducts = createSelector(
  getProductsPageState,
  (state) => state.products
);

export const getTotalProducts = createSelector(
  getProductsPageState,
  (state) => state.totalProducts
);

export const getIsProductsReady = createSelector(
  getProductsPageState,
  (state) => state.isReady
);

export const getIsProductsSaving = createSelector(
  getProductsPageState,
  (state) => state.isSaving
);

// Google maps
export const getGoogleMapsState = createSelector(
  getRootAppState,
  (state) => state.googleMaps
);

// Orders
export const getOrdersState = createSelector(
  getRootAppState,
  (state) => state.orders
);

export const isOrdersLoading = createSelector(
  getOrdersState,
  (state) => state.isLoading
);

export const getIsOrdersReady = createSelector(
  getOrdersState,
  (state) => state.isReady
);

export const getOrders = createSelector(
  getOrdersState,
  (state) => state.orders
);

export const getTotalOrdersCount = createSelector(
  getOrdersState,
  (state) => state.totalOrders
);

export const getIsOrdersSaving = createSelector(
  getOrdersState,
  (state) => state.isSaving
);

// Order lines
export const getOrderLinesState = createSelector(
  getRootAppState,
  (state) => state.orderLines
);

export const getOrderLines = createSelector(
  getOrderLinesState,
  (state) => state.orderLines
);

export const getIsOrderLinesReady = createSelector(
  getOrderLinesState,
  (state) => state.isReady
);

// Drivers
export const getDriversState = createSelector(
  getRootAppState,
  (state) => state.drivers
);

export const getDrivers = createSelector(
  getDriversState,
  (state) => state.drivers
);

export const getTotalDrivers = createSelector(
  getDriversState,
  (state) => state.totalDrivers
);

export const getIsDriversReady = createSelector(
  getDriversState,
  (state) => state.isReady
);

export const getIsDriversSaving = createSelector(
  getDriversState,
  (state) => state.isSaving
);

// Addresses

export const getAddressesState = createSelector(
  getRootAppState,
  (state) => state.addresses
);

// Deliveries
export const getDeliveriesState = createSelector(
  getRootAppState,
  (state) => state.deliveries
);

export const getDeliveries = createSelector(
  getDeliveriesState,
  (state) => state.deliveries
);

export const getIsDeliveriesSaving = createSelector(
  getDeliveriesState,
  (state) => state.isSaving
);

// Suppliers
export const getSuppliersState = createSelector(
  getRootAppState,
  (state) => state.suppliers
);

export const getSuppliers = createSelector(
  getSuppliersState,
  (state) => state.suppliers
);

export const getIsSuppliersReady = createSelector(
  getSuppliersState,
  (state) => state.isReady
);

export const getIsSuppliersSaving = createSelector(
  getSuppliersState,
  (state) => state.isSaving
);

// Contacts
export const getContactsState = createSelector(
  getRootAppState,
  (state) => state.contacts
);

export const getContacts = createSelector(
  getContactsState,
  (state) => state.contacts
);

export const getIsContactsReady = createSelector(
  getContactsState,
  (state) => state.isReady
);

export const getIsContactsSaving = createSelector(
  getContactsState,
  (state) => state.isSaving
);

// Deviations
export const getDeviationsState = createSelector(
  getRootAppState,
  (state) => state.deviations
);

export const getDeviations = createSelector(
  getDeviationsState,
  (state) => state.deviations
);

export const getIsDeviationsReady = createSelector(
  getDeviationsState,
  (state) => state.isReady
);

// Units
export const getUnitState = createSelector(
  getRootAppState,
  (state) => state.units
);

export const getUnits = createSelector(getUnitState, (state) => state.units);

export const getIsUnitsReady = createSelector(
  getUnitState,
  (state) => state.isReady
);

// Delivery time categories
export const getDeliveryTimeCategoriesState = createSelector(
  getRootAppState,
  (state) => state.deliveryTimeCategories
);

export const getDeliveryTimeCategories = createSelector(
  getDeliveryTimeCategoriesState,
  (state) => state.categories
);

export const getIsDeliveryTimeCategoriesReady = createSelector(
  getDeliveryTimeCategoriesState,
  (state) => state.isReady
);

// Delivery times
export const getDeliveryTimesState = createSelector(
  getRootAppState,
  (state) => state.deliveryTimes
);

export const getIsDeliveryTimesActionsInProgress = createSelector(
  getDeliveryTimesState,
  (state) => !!state.savingActionsInProgress || !!state.deleteActionsInProgress
);

// Parsed address
export const getParsedAddressState = createSelector(
  getRootAppState,
  (state) => state.parsedAddress
);

export const getParsedAddress = createSelector(
  getParsedAddressState,
  (state) => state.parsedAddress
);

// Terminal transactions
export const getTerminalTransactionsState = createSelector(
  getRootAppState,
  (state) => state.terminalTransactions
);

export const getTerminalTransactions = createSelector(
  getTerminalTransactionsState,
  (state) => state.terminalTransactions
);

// Page filters
export const getPageFiltersState = createSelector(
  getRootAppState,
  (state) => state.pageFilters
);

export const getDeliveriesPageFilter = createSelector(
  getPageFiltersState,
  (state) => state.deliveriesPage
);

// Export batches
export const getExportBatchesState = createSelector(
  getRootAppState,
  (state) => state.exportBatches
);

export const getExportBatches = createSelector(
  getExportBatchesState,
  (state) => state.exportBatches
);

export const getIsExportBatchesReady = createSelector(
  getExportBatchesState,
  (state) => state.isReady
);

export const getIsExportBatchesSaving = createSelector(
  getExportBatchesState,
  (state) => state.isSaving
);
